import React, { useState, useRef, useEffect } from "react";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import AdminHashtagList from "../shared/AdminHashtagList";

function statusBadge(request) {
  if (request.ghosted_at) return <span className="badge bg-danger">Ghosted</span>;
  if (request.distribution_approved_for_notifications_at) return <span className="badge bg-success">Notified</span>;
  if (request.distribution_approved_for_feed_at) return <span className="badge bg-primary">Feed</span>;
  if (request.approved_at) return <span className="badge bg-secondary">Approved</span>;
  if (request.needs_manual_approval_at) return <span className="badge bg-warning text-dark">Manual</span>;
  if (request.posted_at) return <span className="badge bg-info text-dark">Pending</span>;
  return <span className="badge bg-light text-dark">Draft</span>;
}

const LOGISTICS_OPTIONS = [
  { value: "remote", label: "Remote" },
  { value: "in_person", label: "In Person" },
  { value: "+_video", label: "Video" },
  { value: "pre_recorded", label: "Pre-recorded" },
  { value: "live", label: "Live" },
];

const LOGISTICS_LABELS = Object.fromEntries(LOGISTICS_OPTIONS.map(({ value, label }) => [value, label]));

function RequestDetail({ request, fullRequest, fullRequestError, tags, onFieldSave }) {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const editRef = useRef(null);
  useEffect(() => {
    if (editingField && editRef.current) editRef.current.focus();
  }, [editingField]);

  const canEdit = !!onFieldSave;

  const startEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || "");
    setSaveError(null);
  };

  const startArrayEdit = (field, currentArray) => {
    setEditingField(field);
    setEditValue(currentArray || []);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
    setSaveError(null);
  };

  const confirmEdit = async (overrideValue) => {
    setSaving(true);
    setSaveError(null);
    try {
      let val = overrideValue !== undefined ? overrideValue : editValue;
      // Trim and filter empty strings from array fields
      if (Array.isArray(val)) val = val.map((v) => v.trim()).filter((v) => v !== "");
      await onFieldSave(editingField, val);
      setEditingField(null);
      setEditValue("");
    } catch (err) {
      setSaveError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editButton = (field, currentValue) => {
    if (!canEdit || editingField) return null;
    const needsFullRequest = field === "equipment_notes" || field === "logistics_details" || field === "links";
    if (needsFullRequest && !fullRequest) return null;
    const isArray = field === "logistics_details" || field === "links";
    return (
      <button
        type="button"
        className="btn btn-sm btn-link p-0 ms-2"
        onClick={() => (isArray ? startArrayEdit(field, currentValue) : startEdit(field, currentValue))}
        title={`Edit ${field}`}
      >
        <i className="fas fa-pencil" />
      </button>
    );
  };

  const editControls = (
    <div className="mt-1">
      {saveError && <div className="text-danger small mb-1">{saveError}</div>}
      <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => confirmEdit()} disabled={saving}>
        {saving ? <i className="fa fa-spinner fa-spin" /> : "Save"}
      </button>
      <button type="button" className="btn btn-sm btn-secondary" onClick={cancelEdit} disabled={saving}>
        Cancel
      </button>
      {request.approved_at && !request.ghosted_at && (
        <span className="text-muted small ms-2">
          <i className="fa fa-info-circle me-1" />
          Edit preserves approval state
        </span>
      )}
    </div>
  );

  const details = fullRequest?.details || request.details;

  const renderDetailsSection = () => {
    if (editingField === "details") {
      return (
        <div className="mb-3">
          <textarea
            className="form-control"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancelEdit();
            }}
            maxLength={3000}
            rows={8}
            ref={editRef}
            disabled={saving}
          />
          <div className="text-muted small mt-1">{editValue.length}/3000</div>
          {editControls}
        </div>
      );
    }
    if (details) {
      return (
        <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
          {details}
          {editButton("details", details)}
        </div>
      );
    }
    if (canEdit && !editingField) {
      return (
        <div className="mb-3 text-muted small fst-italic">
          No details
          {editButton("details", "")}
        </div>
      );
    }
    return null;
  };

  const renderEquipmentNotesSection = () => {
    if (editingField === "equipment_notes") {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">Equipment Notes</div>
          <textarea
            className="form-control"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancelEdit();
            }}
            rows={3}
            ref={editRef}
            disabled={saving}
          />
          {editControls}
        </div>
      );
    }
    if (fullRequest?.equipment_notes) {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">
            Equipment Notes
            {editButton("equipment_notes", fullRequest.equipment_notes)}
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{fullRequest.equipment_notes}</div>
        </div>
      );
    }
    if (fullRequest && canEdit && !editingField) {
      return (
        <div className="mb-3 text-muted small fst-italic">
          No equipment notes
          {editButton("equipment_notes", "")}
        </div>
      );
    }
    return null;
  };

  const renderLogisticsSection = () => {
    if (editingField === "logistics_details") {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">Logistics</div>
          {LOGISTICS_OPTIONS.map(({ value, label }) => (
            <div className="form-check" key={value}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`log-${value}`}
                checked={Array.isArray(editValue) && editValue.includes(value)}
                onChange={(e) => {
                  setEditValue((prev) => {
                    const arr = Array.isArray(prev) ? prev : [];
                    return e.target.checked ? [...arr, value] : arr.filter((v) => v !== value);
                  });
                }}
                disabled={saving}
              />
              <label className="form-check-label" htmlFor={`log-${value}`}>
                {label}
              </label>
            </div>
          ))}
          {editControls}
        </div>
      );
    }
    if (fullRequest?.logistics_details?.length > 0) {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">
            Logistics
            {editButton("logistics_details", fullRequest.logistics_details)}
          </div>
          <ul className="mb-0 small">
            {fullRequest.logistics_details.map((item, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${item}-${idx}`}>{LOGISTICS_LABELS[item] || item}</li>
            ))}
          </ul>
        </div>
      );
    }
    if (fullRequest && canEdit && !editingField) {
      return (
        <div className="mb-3 text-muted small fst-italic">
          No logistics
          <button
            type="button"
            className="btn btn-sm btn-link p-0 ms-2"
            onClick={() => startArrayEdit("logistics_details", [])}
            title="Edit logistics_details"
          >
            <i className="fas fa-pencil" />
          </button>
        </div>
      );
    }
    return null;
  };

  const renderLinksSection = () => {
    if (editingField === "links") {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">Links</div>
          <textarea
            className="form-control form-control-sm"
            value={Array.isArray(editValue) ? editValue.join("\n") : editValue}
            onChange={(e) => setEditValue(e.target.value.split("\n"))}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancelEdit();
            }}
            rows={3}
            ref={editRef}
            disabled={saving}
            placeholder="One URL per line"
          />
          <div className="text-muted small mt-1">One URL per line (max 12)</div>
          {editControls}
        </div>
      );
    }
    if (fullRequest?.links?.length > 0) {
      return (
        <div className="mb-3">
          <div className="fw-medium small text-muted mb-1">
            Links
            {editButton("links", fullRequest.links)}
          </div>
          <ul className="mb-0 small">
            {fullRequest.links.map((link, idx) => {
              const isSafe = /^https?:\/\//i.test(link);
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${link}-${idx}`}>
                  {isSafe ? (
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      {link}
                    </a>
                  ) : (
                    <span className="text-muted">{link}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      );
    }
    if (fullRequest && canEdit && !editingField) {
      return (
        <div className="mb-3 text-muted small fst-italic">
          No links
          <button
            type="button"
            className="btn btn-sm btn-link p-0 ms-2"
            onClick={() => startArrayEdit("links", [])}
            title="Edit links"
          >
            <i className="fas fa-pencil" />
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card" style={{ borderLeft: "4px solid #6f42c1" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          {statusBadge(request)}
          <div className="d-flex align-items-center gap-2">
            {globalThis.Qwoted.V1_URL && fullRequest?.migrate_id && (
              <a
                href={`${globalThis.Qwoted.V1_URL}/source_requests/${fullRequest.migrate_id}`}
                className="btn btn-outline-secondary btn-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-external-link-alt me-1" />
                V1
              </a>
            )}
            <span className="text-muted small">#{request.id}</span>
          </div>
        </div>

        {editingField === "title" ? (
          <div className="mb-2">
            <input
              type="text"
              className="form-control"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmEdit();
                if (e.key === "Escape") cancelEdit();
              }}
              maxLength={150}
              ref={editRef}
              disabled={saving}
            />
            {editControls}
          </div>
        ) : (
          <h4 className="mb-2">
            {request.title || "Untitled Request"}
            {editButton("title", request.title)}
          </h4>
        )}

        <div className="d-flex align-items-center mb-3">
          {request.profile_image_url ? (
            <img
              src={request.profile_image_url}
              alt=""
              style={{ width: 36, height: 36 }}
              className="me-2 object-fit-cover rounded-circle flex-shrink-0"
            />
          ) : (
            <div
              className="me-2 bg-light d-flex align-items-center justify-content-center text-muted rounded-circle flex-shrink-0"
              style={{ width: 36, height: 36, fontSize: "0.85rem" }}
            >
              <i className="fa fa-user" />
            </div>
          )}
          <div>
            <span className="text-muted">By </span>
            <strong>{request.profile_name}</strong>
            <span className="text-muted"> at </span>
            {request.media_outlet_name ? (
              <strong>{request.media_outlet_name}</strong>
            ) : (
              <span className="text-muted fst-italic">No outlet</span>
            )}
            {request.free_to_pitch && <span className="badge border border-info text-info ms-2">Free to pitch</span>}
            {request.paid && (
              <span className="text-warning ms-2" title="Fee-based">
                <i className="fa fa-dollar-sign" />
              </span>
            )}
          </div>
        </div>

        {renderDetailsSection()}

        {tags?.length > 0 && <AdminHashtagList tags={tags} />}

        {renderLogisticsSection()}

        {renderEquipmentNotesSection()}

        {renderLinksSection()}

        {fullRequestError && (
          <div className="text-muted small fst-italic mb-3">
            <i className="fa fa-exclamation-circle me-1" />
            Could not load full request content
          </div>
        )}

        <div className="d-flex flex-wrap gap-4 text-muted small">
          {request.posted_at && (
            <div>
              <i className="fa fa-clock-o me-1" />
              Posted {qformat(request.posted_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
          {request.deadline_at && (
            <div>
              <i className="fa fa-calendar me-1" />
              Deadline {qformat(request.deadline_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
          {request.approved_at && (
            <div>
              <i className="fa fa-check me-1" />
              Approved {qformat(request.approved_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
          {request.distribution_approved_for_feed_at && (
            <div>
              <i className="fa fa-rss me-1" />
              Feed {qformat(request.distribution_approved_for_feed_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
          {request.distribution_approved_for_notifications_at && (
            <div>
              <i className="fa fa-bell me-1" />
              Notified {qformat(request.distribution_approved_for_notifications_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
          {request.ghosted_at && (
            <div>
              <i className="fa fa-ban me-1" />
              Ghosted {qformat(request.ghosted_at, DATE_FORMATS.dateTime, null)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequestDetail;
